/**
 * The forecast grid table (f11.1): two header rows -- the group bands and the
 * column headers -- then one row per line, all driven by `FORECAST_COLUMNS`.
 *
 * Reused unchanged by f11.3 (rail entry) and f11.4 (Commitments tab).
 *
 * f11.5: a closed month's header, in every month group, carries a muted
 * "Actual" badge -- read from the server's `months[].closed` only.
 */

import type { ForecastGrid, ForecastLine } from '@/api/client';

import { ForecastRow } from './ForecastRow';
import { FORECAST_COLUMNS, columnBands } from './forecastColumns';
import type { ForecastColumn } from './forecastColumns';
import { ACTUAL_LABEL, isActualMonth } from './monthWindow';
import type { CellChange } from './useUpdateForecastLine';

interface ForecastGridTableProps {
  grid: ForecastGrid;
  lines: readonly ForecastLine[];
  onSave: (line: ForecastLine, change: CellChange) => void;
  columns?: readonly ForecastColumn[];
}

function isActualColumn(column: ForecastColumn, grid: ForecastGrid): boolean {
  return column.month != null && isActualMonth(grid.months.find((m) => m.period_month === column.month));
}

function headerClass(column: ForecastColumn, grid: ForecastGrid): string {
  const kind = isActualColumn(column, grid)
    ? 'col-closed'
    : column.kind === 'identity'
      ? 'col-ro'
      : column.kind === 'calculated'
        ? 'col-calc'
        : 'col-edit';
  const numeric = !['text', 'mono', 'lookup', 'flag'].includes(column.format);
  const sticky = column.field === 'element_code' ? ' sticky-l fg-element' : '';
  return `${kind}${numeric ? ' num' : ''}${sticky}`;
}

export function ForecastGridTable({ grid, lines, onSave, columns = FORECAST_COLUMNS }: ForecastGridTableProps) {
  const bands = columnBands(columns);
  return (
    <div className="fg-wrap">
      <table className="fg">
        <thead>
          <tr className="band">
            {bands.map((band, index) => (
              <th
                key={`${band.group}-${index}`}
                colSpan={band.span}
                className={index === 0 && band.group === 'identity' ? 'sticky-l' : undefined}
              >
                {band.label}
              </th>
            ))}
          </tr>
          <tr className="cols">
            {columns.map((column) => (
              <th key={column.id} className={headerClass(column, grid)} scope="col">
                {isActualColumn(column, grid) ? (
                  <span className="fg-month-head">
                    {column.header}
                    <span className="badge badge-muted fg-actual">{ACTUAL_LABEL}</span>
                  </span>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <ForecastRow key={line.id} line={line} grid={grid} columns={columns} onSave={onSave} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
